import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { GbcapicodeService } from 'app/core/stammdaten-api';
import { TranslateNamePipe } from 'app/shared/pipes/simple/translate-name.pipe';
import { CodeEntry, CodeEntryMeta } from 'app/core/gb-api';
import { Observable, of, Subscription } from 'rxjs';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AuftragRechteSelectionMeta } from 'app/core/batch-auftrag-api';
import { NgbTooltip, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTypeaheadSearch } from '../../../../../shared/components/typeahead-search/abstract-typeahead-search';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { MetaDisablerDirective } from '../../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../../shared/directives/meta-validator.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateNamePipe as TranslateNamePipe_1 } from '../../../../../shared/pipes/simple/translate-name.pipe';

@Component({
  selector: 'cap-auftrag-code-selection',
  templateUrl: './auftrag-code-selection.component.html',
  styleUrls: ['./auftrag-code-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AuftragCodeSelectionComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, NgbTooltip, TranslatePipe, TranslateNamePipe_1]
})
export class AuftragCodeSelectionComponent extends AbstractTypeaheadSearch<CodeEntryMeta> implements ControlValueAccessor, OnDestroy {
  model: AuftragRechteSelectionMeta;

  @Input()
  disabled: boolean;

  public isOpen = false;
  currentTypes: CodeEntry.TypeEnum[] = [];
  codes: Set<CodeEntryMeta> = new Set([]);
  codeMap: Map<CodeEntry.TypeEnum, any[]> = new Map();
  capiCodeTypes: CodeEntry.TypeEnum[] = ['ANMERKUNG', 'VORMERKUNG', 'DIENSTBARKEIT_GRUNDLAST', 'GRUNDSTUECK_UNTER_ART_SDR'];

  private subscription: Subscription;

  propagateChange: (id: Array<CodeEntryMeta>) => void;
  propagateTouch: (_) => void;

  constructor(
    private capiCodeService: GbcapicodeService,
    private translateNamePipe: TranslateNamePipe
  ) {
    super();
    this.showInputAfterSelect = false;
    if (this.capiCodeTypes) {
      this.capiCodeTypes.forEach((code) => {
        this.capiCodeService.loadCapiCodes(code, true).subscribe((data) => {
          this.codeMap.set(code, data);
          if (this.codeMap.size === this.capiCodeTypes.length) {
            this.calculateSearchEntries();
          }
        });
      });
    }
  }

  @Input()
  set types(types: CodeEntry.TypeEnum[]) {
    if (!types || types.length === 0) {
      this.currentTypes = [];
    } else if (JSON.stringify(types) === JSON.stringify(this.currentTypes)) {
      return;
    }
    this.currentTypes = types;
    this.calculateSearchEntries();
  }

  private calculateSearchEntries() {
    const entries: any[] = [];
    if (this.codeMap.size !== 0) {
      this.currentTypes.forEach((type) => {
        entries.push(...this.codeMap.get(type));
      });
    }
    if (this.model && this.codeMap.size !== 0) {
      this.validateSelected();
    }
    return entries;
  }

  writeValue(obj: AuftragRechteSelectionMeta) {
    if (obj) {
      this.model = obj;
      this.codes = new Set(this.model.rechtCodes);
      if (this.currentTypes && this.codeMap.size !== 0) {
        this.validateSelected();
      }
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<CodeEntryMeta>) {
    const selectedItem: CodeEntryMeta = $event.item;
    if (selectedItem) {
      const codesArray = Array.from(this.codes.values());
      codesArray.unshift(selectedItem);
      this.model.rechtCodes = codesArray;
      this.codes = new Set(codesArray);
    }
    this.onClear();
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        return of([]);
      }),
      map(() => {
        if (this.searchTerm.length >= this.minSearchLength) {
          const results = this.calculateSearchEntries().filter(this.filterResults).sort(this.sortResults);
          this.results = results;
          return results;
        }
      })
    );
  };

  formatEntry(entry: CodeEntryMeta): string {
    return this.translateNamePipe.transform(entry?.text);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onDelete(code: any) {
    this.codes.delete(code);
  }

  validateSelected() {
    const filteredArray = [];
    this.currentTypes.forEach((t) => {
      Array.from(this.codes.values()).map((codeEntryMeta) => {
        filteredArray.push(
          ...this.codeMap.get(t).filter((code) => {
            return code.id?.id === codeEntryMeta.id?.id;
          })
        );
      });
    });
    this.codes = new Set(filteredArray);
    this.model.rechtCodes = Array.from(filteredArray);
  }
}
